// eslint-disable-next-line no-unused-vars
import React, { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mutate } from 'swr';
import omit from 'lodash/omit';
import uniqueId from 'lodash/uniqueId';

import download from 'Utils/download';
import useToastNotifications from 'Hooks/useToastNotifications';
import useAsyncError from 'Hooks/useAsyncError';
import useRouter from 'Hooks/useRouter';
import useCart from 'Hooks/useCart';
import useUser from 'Hooks/useUser';
import usePermissions from 'Hooks/usePermissions';
import useWishlists from 'Hooks/useWishlists';
import useFetch from 'Hooks/useFetch';
import useModals from 'Hooks/useModals';
import CheckoutComponent from 'Components/Checkout/Checkout';
import { API_ORDERS, API_USER_CREDIT, API_USER_SOLD, API_CART_EXPORT } from 'Constants';
import OrderModel from 'Containers/OrderModel';
import parseExtraTax from 'Utils/parseExtraTax';
import useAnalytics from 'Hooks/useAnalytics';

const Checkout = memo(() => {
  const { push } = useRouter();
  const formRef = useRef(null);
  const { data: user, getUserData, setUserData, updateUser, mutate: mutateUser } = useUser();
  const { data: cart, hasSupplierProducts, updateProduct, removeProduct, emptyCart } = useCart();
  const toast = useToastNotifications();
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const [orderType, setType] = useState(null);
  const [msg, setMsg] = useState([]);
  const [canReserve, setCanReserve] = useState(!!user?.canPlaceStockReservationOrder);
  const [canPlaceOrder, setCanPlace] = useState(cart?.items?.length > 0);
  const [loading, setLoading] = useState(false);
  const [loadingWishlist, setLoadingWishlist] = useState(false);
  const [selectedDSCountry, setSelectedDSCountry] = useState(192); // Default: Romania - 192
  const [isFormValid, setIsFormValid] = useState(false);
  const [shouldShowErrors, setShouldShowErrors] = useState(false);
  const [unreservableProducts, setUnreservableProducts] = useState([]);
  const [unavailableProductIds, setUnavailableProductIds] = useState([]);

  const fetcher = useFetch();
  const modalsContext = useModals();
  const wishlistContext = useWishlists();

  const canPlaceDropShipmentOrder = 'canPlaceDropShipmentOrder' in user && user.canPlaceDropShipmentOrder;
  const canPlaceEmagOrder = !!user?.canPlaceEmagMarketplaceDropShipmentOrder;
  const canPlaceInternalOrder = !!user?.canPlaceInternalDropShipmentOrder;
  const canPlaceExternalOrder = !!user?.canPlaceExternalDropShipmentOrder;
  const canPlaceStockReservationOrder = 'canPlaceStockReservationOrder' in user ? user.canPlaceStockReservationOrder && !hasSupplierProducts : !hasSupplierProducts;

  const paymentOptionId = user?.userPaymentOption?.id;
  const transportOptionId = user?.userTransportOption?.id;
  const billingAddressId = user?.billingAddressId?.id;
  const deliveryAddressId = user?.deliveryAddressId?.id;
  const { isExternalReseller } = usePermissions();
  const defaultCurrency = user?.defaultCurrency;
  const vat = user?.withVat;
  const userCheckoutData = getUserData('checkout') || {};
  const throwError = useAsyncError();
  const currencyDropdownValues = (['eur', 'usd'].map(item => ({ id: item, label: t(`order.currency.${item}`) })));
  const normalizeTransportValue = value => (value === null || value === undefined || value === '' ? '' : String(value));

  const [loadingEmptyCart, setLoadingEmptyCart] = useState(false);
  const [products, setProducts] = useState(cart?.items);
  const [wishlists, setWishlists] = useState([]);
  // const [selectedCurrency, setSelectedCurrency] = useState();
  const [selectedCurrencyItem, setSelectedCurrencyItem] = useState();
  const [modalOpen, setModalOpen] = useState(false);
  const [order, setOrder] = useState(
    new OrderModel({
      paymentOption: paymentOptionId,
      transportOption: transportOptionId,
      deliveryAddress: deliveryAddressId,
      billingAddress: billingAddressId,
      transportValue: null,
      ...userCheckoutData,
      items: cart?.items?.map(i => ({
        ...omit(i, ['cashOnDeliveryValue']),
      }))
        || userCheckoutData?.items
        || [],
    }),
  );
  const [transportValue, setTransportValue] = useState(normalizeTransportValue(order?.transportValue));

  useEffect(() => {
    setTransportValue(normalizeTransportValue(order?.transportValue));
  }, [order?.transportValue]);

  const productsType = () => {
    if (!products?.length) return null;

    const hasNodProd = products.some(p => p.stockClearingPrice || !!p.originalCode);
    const hasSupplierProd = products.some(p => p.hasPrice === false);

    if (hasNodProd && hasSupplierProd) return 'mixed-products';
    if (hasSupplierProd) return 'supplier-products';
    return 'nod-products';
  };

  useEffect(() => {
    setSelectedCurrencyItem(currencyDropdownValues.find(item => item.id === defaultCurrency));
  }, [defaultCurrency]);

  useEffect(() => {
    if (Array.isArray(products)) {
      setCanReserve(products.filter(p => p.originalCode || p.stockClearingPrice).length === 0);
    }
  }, [products]);

  useEffect(() => {
    setWishlists(
      Object.values(wishlistContext?.data || []).reduce((acc, item) => {
        acc.unshift({
          id: item.id,
          label: item.name,
        });
        return acc;
      }, [{
        id: uniqueId('create'),
        createButton: true,
        onClick: () => modalsContext.setState(modalsContext.modalState.CREATE_WISHLIST.key, {
          open: true,
          moveProductsFromCart: true,
          moveProductsFromCartCallback: async listData => {
            moveProductsFromCart(listData.id);
            analytics.track('moved_cart_to_new_wishlist');
            await emptyCart();
            setUserData({ checkout: {} });
            toast.addNotification({
              title: t('cart.movedToWishlist'),
            });
            window.scrollTo({ top: 0, behavior: 'smooth' });
          },
        }),
      }]),
    );
  }, [wishlistContext.data]);

  useEffect(() => {
    if (user?.email) {
      setOrder(prevOrder => {
        const prevData = prevOrder.getData();
        const prevItems = prevData.items;
        const existingCheckout = (user?.checkout || {});
        const existingItems = existingCheckout.items || [];
        // TODO: let the user chose whether to keep existing cart, merge with old one or discard it
        const allItems = [...prevItems, ...existingItems];
        const mergedItems = allItems.reduce((acc, item) => {
          const existingIdx = acc.findIndex(i => {
            // TEST: duplicated items, send event only if conditions differ
            const oldCondition = i?.id === item?.id && i?.supplierId === item?.supplierId;
            const newCondition = (i?.id || i?.productId) === (item?.id || item?.productId)
              && (i?.supplierId === item?.supplierId || (!i?.supplierId && !item?.supplierId));
            if (oldCondition !== newCondition) {
              analytics.track('checkout_item_condition_changed', {
                payload: {
                  oldCondition,
                  newCondition,
                  item,
                  i,
                }
              });
            }
            // TEST: duplicated items, send event only if conditions differ
            if (item.appliedPromotions) {
              return (
                item.appliedPromotions.at(0) === i.appliedPromotions?.at(0)
                && i.id === item.id
              );
            }
            return (
              (i.id || i?.productId) === (item.id || item?.productId)
              && (i?.supplierId === item.supplierId || (!i?.supplierId && !item.supplierId))
            );
          });
          if (existingIdx === -1) {
            acc.push(item);
          } else {
            acc.splice(existingIdx, 1, {
              ...acc[existingIdx],
              ...item,
            });
          }
          return acc;
        }, []);
        return new OrderModel({
          ...prevData,
          ...existingCheckout,
          items: mergedItems,
        });
      });
    }
  }, [user?.email]);

  useEffect(() => {
    if (
      paymentOptionId
      && paymentOptionId !== order.paymentOption
    ) {
      order.setPaymentOption(paymentOptionId);
    }
  }, [paymentOptionId]);

  useEffect(() => {
    if (
      transportOptionId
      && order.transportOption
      && transportOptionId !== order.transportOption
    ) {
      order.setTransportOption(transportOptionId);
    }
  }, [transportOptionId]);

  useEffect(() => {
    if (
      deliveryAddressId
      && deliveryAddressId !== order.deliveryAddress
    ) {
      order.setDeliveryAddress(deliveryAddressId);
    }
  }, [deliveryAddressId]);

  useEffect(() => {
    if (
      billingAddressId
      && billingAddressId !== order.billingAddress
    ) {
      order.setBillingAddress(billingAddressId);
    }
  }, [billingAddressId]);

  useEffect(() => {
    let canPlace = cart?.items?.length > 0;
    const stockErrorMessages = (cart.items || []).reduce((acc, prod) => {
      if (
        prod.stockValue === 0
        || prod.minQuantity > prod.stockValue
      ) {
        canPlace = false;
        acc.push({
          type: 'error',
          msg: t('product.unavailable', {
            name: prod.title.length > 25
              ? `${ prod.title.slice(0, 25) }...`
              : prod.title,
          }),
        });
      }
      if (prod.quantity > prod.stockValue) {
        canPlace = false;
      }

      // check the promo items stock
      if (prod.promoItems) {
        Object.values(prod.promoItems).forEach(promoItemsArray => {
          if (Array.isArray(promoItemsArray)) {
            promoItemsArray.forEach(promoItem => {
              if (promoItem.quantity > promoItem.stockValue) {
                canPlace = false;
                acc.push({
                  type: 'error',
                  msg: t('product.unavailableStock', {
                    name: promoItem.title?.length > 25
                      ? `${ promoItem.title.slice(0, 25) }...`
                      : promoItem.title || 'Promo item',
                  }),
                });
              }
            });
          }
        });
      }
      return acc;
    }, []);

    if (stockErrorMessages.length) {
      setMsg(stockErrorMessages);
    } else {
      setMsg([]);
    }

    // remove applied promotions from recommended
    const appliedPromotionIds = (cart.items || [])
      .map(item => item.promotion?.promotionId)
      .filter(Boolean);

    const updatedProducts = (cart.items || []).map(item => ({
      ...item,
      availablePromotions: (item.availablePromotions || []).filter(
        promo => !appliedPromotionIds.includes(promo.promotionId)
      )
    }));

    setCanPlace(canPlace);
    setProducts(updatedProducts);

    if (cart.items) {
      if (!cart.items.length) {
        order.removeItems();
      } else {
        order.items.forEach(orderItem => {
          if (!cart.items.find(cartItem => {
            if (cartItem.promotion?.promotionId) {
              return (
                cartItem.productId === orderItem.id
                && cartItem.promotion.promotionId === orderItem.appliedPromotions?.at(0)
              );
            }
            return (
              cartItem.productId === orderItem.id
              && (cartItem?.supplierId === orderItem.supplierId || (!cartItem?.supplierId && !orderItem.supplierId))
              && !orderItem.appliedPromotions
            );
          })) {
            order.removeItem(orderItem);
          }
        });
        cart.items.forEach(cartItem => {
          const item = {
            ...cartItem,
          };
          if (cartItem.promotion?.promotionId) {
            item.appliedPromotions = [cartItem.promotion.promotionId];
            item.promoItems = Object
              .entries(cartItem.promoItems)
              .reduce((acc, [promoKey, promoItems]) => {
                acc[promoKey] = promoItems.map(promoItem => omit(promoItem, ['cashOnDeliveryValue']));
                return acc;
              }, []);
          }
          order.updateItem(omit(item, ['cashOnDeliveryValue']));
        });
      }
    }
  }, [cart?.items]);

  useEffect(() => {
    const unsubscribe = order.subscribe(data => {
      setUserData({
        checkout: {
          ...order.getData(),
          ...data,
        },
      });
      setOrder(prevOrder => new OrderModel({
        ...prevOrder.getData(),
        ...data,
      }));
    });

    return () => {
      unsubscribe();
    };
  }, [order]);

  useEffect(() => {
    if (
      !user?.canPlaceDropShipmentOrder
      && order.dropShipment
    ) {
      order.setIsDropShipping(false);
    }
  }, [user.canPlaceDropShipmentOrder, order.dropShipment]);

  const moveProductsFromCart = async listId => {
    setLoadingWishlist(true);

    try {
      const existingWishlist = wishlistContext?.data[listId] || { products: [] };

      const productsToAdd = cart.items
        .filter(cartItem => {
          const isNotPromotion = !cartItem.promotion;
          const notInWishlist = existingWishlist.products?.findIndex(
            prod => prod.id === cartItem.productId
          ) === -1;

          return isNotPromotion && notInWishlist;
        })
        .map(cartItem => ({
          id: cartItem.productId,
          quantity: Math.max(
            cartItem?.quantity || 1,
            cartItem?.minQuantity || 1
          ),
        }));

      await wishlistContext.updateProducts(listId, productsToAdd);
      await emptyCart();

      setUserData({ checkout: {} });

      toast.addNotification({
        title: t('cart.movedToWishlist'),
      });

      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.addNotification({
        type: 'error',
        title: t('wishlists.failedUpdatingWishlist'),
      });
    } finally {
      setLoadingWishlist(false);
    }
  };

  const handleSetTransportValue = ({ target: { value } }) => {
    setTransportValue(value);

    if (value === '') {
      order.setTransportValue(null);
      return;
    }

    const decimalsToShow = 2;
    const extraTax = parseExtraTax(value, decimalsToShow);

    order.setTransportValue(extraTax);
  };

  const handlePlaceOrder = async (evt, type) => {
    evt.preventDefault();

    const valid = formRef?.current?.checkValidity() || false;

    if (!isFormValid) {
      setShouldShowErrors(true);
      toast.addNotification({
        type: 'warning',
        title: t('order.formIncomplete'),
      });
    } else if (!valid) {
      toast.addNotification({
        type: 'warning',
        title: t('order.finalPriceMissing'),
      });
    }

    // stop if form not valid by html validation standards
    if (
      !valid
      || !isFormValid
      || !canPlaceOrder
    ) return;

    try {
      setLoading(true);
      setUnavailableProductIds([]);
      /* prepare order body */
      const orderBody = {
        ...order.getData(),
        stockReservation: type === 'reservation',
        currency: isExternalReseller ? defaultCurrency : 'ron'
      };
      if (orderBody.transportValue === null) {
        orderBody.transportValue = 0;
      }
      const analyticsOrderPlaced = {
        order_type: type === 'reservation' ? 'stock_reservation' : 'normal_order',
        currency: isExternalReseller ? defaultCurrency : 'ron',
        is_drop_shipment: orderBody.dropShipment,
        transport_value: orderBody.transportValue,
        delivery_type: orderBody.deliveryAddressData?.deliveryType,
        delivery_mode: orderBody.deliveryAddressData?.deliveryMode,
        country: orderBody.deliveryAddressData?.country,
        transport_option: orderBody.transportOption,
        items: cart.items.map(item => ({
          product_id: item?.id || item?.productId,
          quantity: item.quantity,
          product_type: item.supplierId ? 'external_supplier' : 'regular_product',
          manufacturer: item.manufacturer,
          category_name: item.categoryName,
        }))
      };
      setType(type);
      if (orderBody.dropShipment) {
        const { deliveryAddressData: data } = orderBody;

        orderBody.deliveryAddress = { ...data };
        orderBody.deliveryAddress.email = data.contactPersonRecipientEmail;
        orderBody.ref = data.deliveryType;

        const isEmktpLocker = data.deliveryType === 'emktp' && data.deliveryMode === 'locker';

        if (isEmktpLocker) {
          const lockerId = data.deliveryLockerCode;
          const street = data.address || '';
          const lockerTag = `[EasyBox #${lockerId}]`;
          const regex = new RegExp(`\\[EasyBox #${lockerId}\\]`);

          if (!regex.test(street)) {
            orderBody.deliveryAddress.address = `${lockerTag} ${street}`;
          }

          orderBody.clientEmagMarketplaceLockerId = lockerId;
        } else {
          orderBody.deliveryLockerCode = data.deliveryLockerCode || data.deliveryLockerId;
        }
      } else {
        orderBody.transportValue = 0;
      }

      delete orderBody.deliveryAddressData;

      if (orderBody.orderNotes?.length) {
        orderBody.comment = orderBody.orderNotes;
      }
      delete orderBody.orderNotes;
      orderBody.items = orderBody.items
        .reduce((orderItemsAcc, orderItem) => {
          // filter order items so that we make sure products
          // in order body are also found inside the cart
          // usually we shouldn't have to check for this but apparently there
          // are edge cases where orderItems !== cartItems
          if (
            !cart.items.find(i => {
              if (
                orderItem?.appliedPromotions?.length
                && i.promotion?.promotionId
              ) {
                return (
                  i.productId === orderItem.id
                  && i.promotion.promotionId === orderItem.appliedPromotions.at(0)
                );
              }
              return i.productId === orderItem.id;
            })
          ) {
            return orderItemsAcc;
          }

          const payload = {
            id: orderItem.id,
            quantity: orderItem.quantity,
            minQuantity: orderItem.minQuantity,
            ...(!orderItem.hasPrice && {
              supplierId: orderItem.supplierId,
              supplierOfferId: orderItem.supplierOfferId,
            })
          };
          if (orderBody.dropShipment) {
            payload.cashOnDeliveryValue = orderItem.cashOnDeliveryValue;
          }
          if (
            !orderItem.appliedPromotions
            || !orderItem.appliedPromotions.length
          ) {
            // eslint-disable-next-line no-param-reassign
            delete orderItem.promoItems;
            // eslint-disable-next-line no-param-reassign
            delete orderItem.appliedPromotions;
          }
          if (orderItem.promoItems) {
            payload.appliedPromotions = orderItem.appliedPromotions;
            if (orderBody.dropShipment) {
              payload.promoItems = Object
                .entries(orderItem.promoItems)
                .reduce((acc, [promoKey, promoItems]) => {
                  acc[promoKey] = promoItems.map(promoItem => ({
                    id: promoItem.id || promoItem.productId,
                    cashOnDeliveryValue: promoItem.cashOnDeliveryValue,
                    quantity: promoItem.quantity,
                    minQuantity: promoItem.minQuantity,
                  }));
                  return acc;
                }, {});
            }
          }

          orderItemsAcc.push(payload);
          return orderItemsAcc;
        }, []);
      // TEST: duplicated items, send event only if conditions differ
      if ((orderBody?.items || []).some((item, idx, arr) => arr.findIndex(i => i?.id === item?.id) !== idx)) {
        analytics.track('has_duplicates');
      }
      // TEST: duplicated items, send event only if conditions differ
      analytics.track('placed_order', {
        ...analyticsOrderPlaced,
      });
      /* create order */
      const response = await fetcher(API_ORDERS, {
        method: 'POST',
        body: JSON.stringify(orderBody),
      }).catch(err => {
        if (err?.status < 500) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          const invalidStockProducts = [];
          const invalidProductIds = [];
          const errors = [];
          let hasAddedStockErrorToOtherErrors = false;
          if (err.info.status === 'error' && err.info.errors) {
            err.info.errors.forEach(error => {
              if (error.code === 'order.items.reservation_stock.invalid_reservation_stock') {
                const itemIndex = parseInt(error.path[1], 10);
                if (products[itemIndex]) {
                  invalidStockProducts.push(products[itemIndex].code);
                }
                if (!hasAddedStockErrorToOtherErrors) {
                  errors.push(error);
                  hasAddedStockErrorToOtherErrors = true; // Ensure this only happens once
                }
              } else {
                if (error.code === 'order.items.product.not_blank') {
                  const productId = error.message_parameters?.product_id;
                  if (productId !== null && productId !== undefined) {
                    invalidProductIds.push(String(productId));
                  }
                }
                errors.push(error);
              }
            });
            if (invalidStockProducts.length) {
              setUnreservableProducts([...invalidStockProducts]);
            }
            setUnavailableProductIds([...new Set(invalidProductIds)]);
          }
          const messages = errors.map(i => i?.message).join('\r\n');
          setMsg([{ type: 'error', msg: messages }]);
        } else {
          throwError(err);
        }
      });
      /* update app after order creation */
      if (response?.length) {
        mutate([user.email, API_USER_SOLD]);
        mutate([user.email, API_USER_CREDIT]);

        emptyCart();
        setUserData({ checkout: {} });

        setMsg([{ type: 'success', msg: t('orders.success') }]);
        setType(null);
        setLoading(false);

        const orderResponsePayload = response.map(item => {
          return {
            products: [],
            // products: order.dropShipment
            //   ? products.map(product => {
            //     const orderItem = order.items.find(item => {
            //       if (
            //         product.promotion
            //         && item.appliedPromotions?.length
            //       ) {
            //         return (
            //           item.id === product.productId
            //           && product.promotion.promotionId === item.appliedPromotions.at(0)
            //         );
            //       }
            //       return item.id === product.id && !item.appliedPromotions;
            //     });
            //     if (product.promotion?.promotionId) {
            //       return {
            //         ...product,
            //         cashOnDeliveryValue: orderItem?.cashOnDeliveryValue || 0,
            //         promoItems: Object
            //           .entries(product.promoItems)
            //           .reduce((promoAcc, [promoKey, promoItems]) => {
            //             const orderPromoItems = orderItem?.promoItems[promoKey] || {};
            //             // eslint-disable-next-line no-param-reassign
            //             promoAcc[promoKey] = promoItems.map(promoItem => {
            //               const orderPromoItem = (orderPromoItems?.find(pi => pi.id === promoItem.productId) || {});
            //               return {
            //                 ...promoItem,
            //                 cashOnDeliveryValue: orderPromoItem?.cashOnDeliveryValue || 0,
            //               };
            //             });
            //             return promoAcc;
            //           }, {}),
            //       };
            //     }
            //     return {
            //       ...product,
            //       cashOnDeliveryValue: orderItem?.cashOnDeliveryValue || 0,
            //     };
            //   })
            //   : products,
            order: {
              // ...orderBody,
              stockReservation: type === 'reservation',
              ...(item || {}),
            },
          };
        });

        push('/checkout/success', { orders: orderResponsePayload });
      } else {
        setMsg((response.messages || []).map(message => ({ type: 'error', msg: message })));
      }
      analytics.track('placed_order_success', {
        ...analyticsOrderPlaced,
      });
    } catch (err) {
      const { error = {} } = err?.info || {};
      setLoading(false);
      if (error?.message) {
        toast.addNotification({
          type: 'error',
          title: t('order.placeOrderError'),
        });
      }
      if (error.message) {
        setMsg([{
          type: 'error',
          msg: error?.message,
        }]);
      } else if (Array.isArray(error.items)) {
        setMsg(error.items.map(errItem => ({
          type: 'error',
          msg: `Product id ${ errItem.data.id } could not be added to cart.`,
        })));
      }
      // TODO: show specific error
      // setMsg(prevMsg => prevMsg
      //   .filter(m => m.type !== 'error')
      //   .concat({
      //     type: 'error',
      //     msg: err.info?.result?.items?.map(i => Object.values(i.errors.id)[0]).join('')
      //       || err.message
      //   })
      // );
    }
  };

  const handleMoveToWishlist = ({ selectedItem = null }) => {
    analytics.track('moved_cart_to_wishlist');

    const listId = selectedItem?.id;
    const isValidWishlist = listId && !selectedItem?.createButton;

    if (isValidWishlist) {
      moveProductsFromCart(listId);
    }
  };

  const handleOnEmptyCart = async () => {
    analytics.track('empty_cart');
    try {
      setLoadingEmptyCart(true);
      await emptyCart();
      order.setIsDropShipping(false);
      setUserData({ checkout: {} });
      setOrder(new OrderModel({}));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoadingEmptyCart(false);
    }
  };

  const handleOnCurrencyChange = async currencyValue => {
    try {
      await updateUser({ defaultCurrency: currencyValue, withVat: vat });
      mutateUser();
    } catch (err) {
      toast.addNotification({
        type: 'error',
        title: t('order.couldNotChangeCurrency')
      });
    }
  };

  return (
    <CheckoutComponent
      order={ order }
      canPlaceDropShipmentOrder={ canPlaceDropShipmentOrder }
      canPlaceStockReservationOrder={ canPlaceStockReservationOrder }
      orderType={ orderType }
      formRef={ formRef }
      products={ products }
      messages={ msg }
      wishlists={ wishlists }
      canPlaceOrder={ canPlaceOrder }
      canReserve={ canReserve }
      loadingSubmit={ loading }
      loadingWishlist={ loadingWishlist }
      onRemoveProduct={ removeProduct }
      onUpdateProduct={ updateProduct }
      transportValue={ transportValue }
      setTransportValue={ handleSetTransportValue }
      onAddToWishlist={ handleMoveToWishlist }
      onSubmit={ handlePlaceOrder }
      loadingEmptyCart={ loadingEmptyCart }
      selectedDSCountry={ selectedDSCountry }
      setSelectedDSCountry={ setSelectedDSCountry }
      canPlaceEmagOrder={ canPlaceEmagOrder }
      canPlaceInternalOrder={ canPlaceInternalOrder }
      canPlaceExternalOrder={ canPlaceExternalOrder }
      emptyCartDisabled={ order.items?.length === 0 }
      onEmptyCart={ handleOnEmptyCart }
      isExternalReseller={ isExternalReseller }
      onCurrencyChange={ handleOnCurrencyChange }
      onCartDownload={ () => download(API_CART_EXPORT) }
      selectedCurrencyItem={ selectedCurrencyItem }
      currencyDropdownValues={ currencyDropdownValues }
      onModalOpen={ () => setModalOpen(true) }
      onModalClose={ () => setModalOpen(false) }
      isModalOpen={ modalOpen }
      setIsFormValid={ setIsFormValid }
      shouldShowErrors={ shouldShowErrors }
      unreservableProducts={ unreservableProducts }
      unavailableProductIds={ unavailableProductIds }
      numberOfOrders={ new Set((products || []).map(p => p.supplierId)).size }
      hasSupplierProducts={ hasSupplierProducts }
      productsType={ productsType() }
      totalItems={ cart?.totalItems }
      orderLines={ cart?.orderLines }
    />
  );
});
Checkout.displayName = 'CheckoutContainer';

export default Checkout;
